import {
  BorderRadiusProps,
  DisplayProps,
  FontSizeProps,
  FontWeightProps,
  LineHeightProps,
  MinWidthProps,
  SpaceProps,
  WidthProps,
  borderRadius,
  display,
  fontSize,
  fontWeight,
  lineHeight,
  minWidth,
  space,
  width,
  compose,
} from 'styled-system';
import Label, { LabelProps } from 'components/_shared/elements/label';
import {
  useState,
  InputHTMLAttributes,
  LabelHTMLAttributes,
  useId,
} from 'react';
import { css } from '@emotion/react';
import styled from 'components/styled';

/**
 * base 64 encoding this SVG (not encoding at runtime for performance reasons)
 * used to be an inline SVG, but emotion doesn't seem to like that anymore and I can't be asked to figure out why not
 * also used to use the theme.colors.black, but not worth the effort here
 *
 * if the color or svg need to be updated:
 *  - update commented out `caret`
 *  - base64 encode it (online or something)
 *  - and replace `caretEncoded` with the new encoded version (obvs)
 */

// const caret =
//   '<svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="32" height="16" viewBox="0 0 32 16">' +
//   '<polygon points="0,0 32,0 16,16" style="fill: rgb(68, 68, 68)"></polygon></svg>';

const caretEncoded =
  'PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZlcnNpb249IjEuMSIgd2lkdGg9IjMyIgogICAgICBoZWlnaHQ9IjE2IiB2aW' +
  'V3Qm94PSIwIDAgMzIgMTYiPjxwb2x5Z29uIHBvaW50cz0iMCwwIDMyLDAgMTYsMTYiIHN0eWxlPSJmaWxsOiByZ2IoNjgsIDY4LCA2OCkiPjwvcG9' +
  'seWdvbj48L3N2Zz4=';

interface StyledSelectProps {
  invalid?: boolean;
}

type Props = StyledSelectProps &
  SpaceProps &
  FontSizeProps &
  WidthProps &
  FontWeightProps &
  LineHeightProps &
  BorderRadiusProps &
  MinWidthProps &
  DisplayProps;

const StyledSelect = styled.select<Props>`
  ${({ invalid = false, theme }) => {
    return css`
      outline: none;
      appearance: none;
      display: block;
      width: 100%;
      font-family: ${theme.fonts.body};
      font-size: ${theme.fontSizes[2]};
      color: inherit;
      background-color: transparent;
      border-width: 2px;
      border-style: solid;
      border-color: ${theme.colors.borderColor};
      padding: ${theme.space[2]}px 12px;
      margin: 0;

      ${invalid &&
      css`
        border-color: ${theme.colors.pink};
      `}

      &:active,
      &:focus {
        border-color: ${theme.colors.primary};
      }

      ::placeholder {
        color: ${theme.colors.mediumGrey};
      }
      ::-ms-clear {
        display: none;
      }

      background-image: url('data:image/svg+xml;base64,${caretEncoded}');

      background-origin: content-box;
      background-position: right -${theme.space[3] * 0.75}px center;
      background-repeat: no-repeat;
      background-size: 9px 6px;
      padding-right: ${theme.space[3] * 1.5}px !important;
    `;
  }}
  ${compose(
    space,
    fontSize,
    width,
    fontWeight,
    lineHeight,
    borderRadius,
    minWidth,
    display
  )}
`;

StyledSelect.defaultProps = {
  borderRadius: 1,
};

export type SelectProps = {
  label?: string;
  labelProps?: LabelProps;
} & InputHTMLAttributes<HTMLSelectElement> &
  Props;

const Select = ({ id, ...props }: SelectProps) => {
  const [isFocused, setFocus] = useState(false);
  const idRef = useId();
  const htmlId = id || `${props.name}-${idRef}`;

  return (
    <>
      {props.label && (
        <Label
          mb={2}
          htmlFor={htmlId}
          invalid={props.invalid}
          isFocused={isFocused}
          {...props.labelProps}
        >
          {props.label}
        </Label>
      )}
      <StyledSelect
        {...props}
        id={htmlId}
        onFocus={e => {
          setFocus(true);
          props.onFocus && props.onFocus(e);
        }}
        onBlur={e => {
          setFocus(false);
          props.onBlur && props.onBlur(e);
        }}
      />
    </>
  );
};

export default Select;
